import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type Toast = { id: number; message: string };

type ToastContextValue = (message: string) => void;

const Ctx = createContext<ToastContextValue | null>(null);

export function useDripToast(): ToastContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDripToast requires DripToastProvider');
  return v;
}

export function DripToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const push = useCallback((message: string) => {
    const id = ++idRef.current;
    setToasts((t) => [...t, { id, message }]);
    window.setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 4200);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-6 left-0 right-0 z-[500] flex flex-col items-center gap-2 px-4"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className="pointer-events-auto max-w-md rounded-xl border border-white/[0.12] bg-white/[0.12] px-4 py-3 text-sm text-[var(--home-text-on-dark)] shadow-lg shadow-black/35 ring-1 ring-white/[0.08] backdrop-blur-md"
          >
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
